import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { debounce } from 'lodash';
import Theme from '../utils/theme.js';

// Fix for default markers in Leaflet with Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default (function () {
  
  // Store map instance for cleanup
  let mapInstance = null;
  let vehicleMarkers = [];
  let updateInterval = null;
  let vehicleData = []; // Store vehicle data from dashboard controls
  
  // Main initialization function
  const leafletMapInit = () => {
    try {
      const mapContainer = document.getElementById('world-map-marker');
      if (!mapContainer) {
        // Silenciar este warning ya que no todas las páginas tienen mapa
        // window.GPSLogger?.debug('Map container not found (normal en páginas sin mapa)');
        return;
      }
      
      // Clear existing map
      if (mapInstance) {
        try {
          mapInstance.remove();
        } catch (error) {
          console.warn('Error removing existing map:', error);
        }
        mapInstance = null;
      }
      
      // Clear existing interval
      if (updateInterval) {
        clearInterval(updateInterval);
        updateInterval = null;
      }
      
      // Clear existing content
      mapContainer.innerHTML = '';
      
      // Create map container
      const leafletContainer = document.createElement('div');
      leafletContainer.id = 'leaflet-map';
      leafletContainer.style.height = '490px';
      leafletContainer.style.width = '100%';
      leafletContainer.style.borderRadius = '0px';
      leafletContainer.style.border = 'none';
      leafletContainer.style.boxShadow = 'none';
      
      mapContainer.appendChild(leafletContainer);
      
      // Initialize map centered on Lima, Peru
      mapInstance = L.map('leaflet-map', {
        center: [-12.0464, -77.0428],
        zoom: 12,
        zoomControl: true,
        scrollWheelZoom: true,
        doubleClickZoom: true,
        boxZoom: true,
        keyboard: true,
        dragging: true,
        touchZoom: true,
        attributionControl: false // Disabled for cleaner look
      });
      
      // Get current theme
      const isDark = Theme.current() === 'dark';
      
      // Add tiles based on theme
      if (isDark) {
        // Dark theme with white text - CartoDB Dark
        L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
          attribution: '', // Removed attribution for cleaner look
          maxZoom: 19,
          minZoom: 3
        }).addTo(mapInstance);
      } else {
        // Light theme tiles
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '', // Removed attribution for cleaner look
          maxZoom: 19,
          minZoom: 3
        }).addTo(mapInstance);
      }
      
      // Scale control removed for cleaner look
      
      // Add vehicle markers
      addVehicleMarkers();
      
      // Store map instance
      mapContainer.mapInstance = mapInstance;
      
    } catch (error) {
      console.error('Error initializing Leaflet map:', error);
    }
  };
  
  // Add vehicle markers for GPS tracking
  const addVehicleMarkers = () => {
    if (!mapInstance) return;
    
    // Clear existing markers
    vehicleMarkers.forEach(marker => {
      try {
        mapInstance.removeLayer(marker);
      } catch (error) {
        console.warn('Error removing marker:', error);
      }
    });
    vehicleMarkers = [];
    
    // Use vehicle data from dashboard controls if available, otherwise use sample data
    const vehicles = vehicleData.length > 0 ? vehicleData : [
      {
        id: 'VH001',
        placa: 'A1F-833',
        chofer: 'Vehículo 1',
        lat: -12.0464,
        lng: -77.0428,
        status: 'parado',
        speed: 0,
        lastUpdate: new Date()
      },
      {
        id: 'VH002', 
        placa: 'B2G-444',
        chofer: 'Vehículo 2',
        lat: -12.0564,
        lng: -77.0328,
        status: 'transmisionNormal',
        speed: 45,
        lastUpdate: new Date()
      },
      {
        id: 'VH003',
        placa: 'C3H-555',
        chofer: 'Vehículo 3', 
        lat: -12.0364,
        lng: -77.0528,
        status: 'excesoVelocidad',
        speed: 95,
        lastUpdate: new Date()
      }
    ];
    
    // Create custom icons based on vehicle status
    const createVehicleIcon = (status) => {
      let iconClass = 'vehicle-marker';
      let iconColor = 'moving';
      
      switch (status) {
        case 'parado':
          iconClass += ' stopped';
          iconColor = 'stopped';
          break;
        case 'sinCobertura':
          iconClass += ' no-coverage';
          iconColor = 'no-coverage';
          break;
        case 'excesoVelocidad':
          iconClass += ' speeding';
          iconColor = 'speeding';
          break;
        case 'transmisionNormal':
          iconClass += ' moving';
          iconColor = 'moving';
          break;
        case 'mantenimiento':
          iconClass += ' maintenance';
          iconColor = 'maintenance';
          break;
        default:
          iconClass += ' moving';
          iconColor = 'moving';
      }
      
      return L.divIcon({
        className: iconClass,
        html: `<div class="marker-icon ${iconColor}"><i class="fas fa-car"></i></div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });
    };
    
    // Add markers to map
    vehicles.forEach(vehicle => {
      try {
        // Validate vehicle data
        if (!vehicle || typeof vehicle.lat !== 'number' || typeof vehicle.lng !== 'number') {
          console.warn('Invalid vehicle data:', vehicle);
          return;
        }
        
        const icon = createVehicleIcon(vehicle.status);
        
        const marker = L.marker([vehicle.lat, vehicle.lng], { icon })
          .addTo(mapInstance)
          .bindPopup(`
            <div class="vehicle-popup">
              <h6><i class="fas fa-car"></i> ${vehicle.placa || vehicle.name || 'N/A'}</h6>
              <p><strong>Chofer:</strong> ${vehicle.chofer || 'N/A'}</p>
              <p><strong>Estado:</strong> ${getStatusText(vehicle.status)}</p>
              <p><strong>Velocidad:</strong> ${vehicle.speed || 0} km/h</p>
              <p><strong>Última actualización:</strong> ${vehicle.lastUpdate ? vehicle.lastUpdate.toLocaleTimeString() : 'N/A'}</p>
            </div>
          `);
        
        vehicleMarkers.push(marker);
      } catch (error) {
        console.error('Error creating marker for vehicle:', vehicle, error);
      }
    });
  };
  
  // Get status text in Spanish
  const getStatusText = (status) => {
    const statusMap = {
      'parado': 'Parado',
      'sinCobertura': 'Sin Cobertura',
      'excesoVelocidad': 'Exceso de Velocidad',
      'transmisionNormal': 'Transmisión Normal',
      'mantenimiento': 'Mantenimiento',
      'moving': 'En Movimiento',
      'stopped': 'Detenido'
    };
    return statusMap[status] || 'Desconocido';
  };

  // Update vehicle positions (for real-time tracking)
  const updateVehiclePositions = () => {
    if (!mapInstance || vehicleMarkers.length === 0) return;
    
    try {
      // Get updated vehicle data from dashboard controls
      if (window.dashboardControls && window.dashboardControls.vehicles) {
        vehicleData = window.dashboardControls.vehicles;
        addVehicleMarkers(); // Refresh markers with new data
      } else {
        // Simulate real-time updates (replace with actual GPS data)
        vehicleMarkers.forEach((marker, index) => {
          try {
            const currentPos = marker.getLatLng();
            if (currentPos && typeof currentPos.lat === 'number' && typeof currentPos.lng === 'number') {
              const newLat = currentPos.lat + (Math.random() - 0.5) * 0.001;
              const newLng = currentPos.lng + (Math.random() - 0.5) * 0.001;
              
              marker.setLatLng([newLat, newLng]);
            }
          } catch (error) {
            console.warn('Error updating marker position:', error);
          }
        });
      }
    } catch (error) {
      console.error('Error updating vehicle positions:', error);
    }
  };

  // Set vehicle data from external source
  const setVehicleData = (data) => {
    vehicleData = data;
    if (mapInstance) {
      addVehicleMarkers();
    }
  };
  
  // Initialize map only if container exists
  const initMapIfNeeded = () => {
    if (document.getElementById('world-map-marker')) {
      leafletMapInit();
      
      // Update positions every 5 seconds (real-time tracking)
      updateInterval = setInterval(updateVehiclePositions, 5000);
    }
  };
  
  // Try to initialize immediately, or wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMapIfNeeded);
  } else {
    initMapIfNeeded();
  }
  
  // Reinitialize on window resize
  window.addEventListener('resize', debounce(leafletMapInit, 300));
  
  // Listen for theme changes
  window.addEventListener('adminator:themeChanged', debounce(leafletMapInit, 150));
  
  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    if (updateInterval) {
      clearInterval(updateInterval);
    }
    if (mapInstance) {
      try {
        mapInstance.remove();
      } catch {
        // Map cleanup on unload
      }
      mapInstance = null;
    }
  });
  
  // Store map instance globally for external access
  window.leafletMapInstance = {
    init: leafletMapInit,
    updatePositions: updateVehiclePositions,
    getInstance: () => mapInstance,
    getMarkers: () => vehicleMarkers,
    setVehicleData: setVehicleData,
    setView: (latlng, zoom) => {
      if (mapInstance) {
        mapInstance.setView(latlng, zoom);
      }
    }
  };

  // Return public API
  return {
    init: leafletMapInit,
    updatePositions: updateVehiclePositions,
    getInstance: () => mapInstance,
    getMarkers: () => vehicleMarkers,
    setVehicleData: setVehicleData
  };
}());
